/**
 * InfoPane positioning utilities for TTMarker component.
 * Pure functions for calculating optimal InfoPane placement.
 */

import { InfoPanePosition } from './TTMarker.types';
import { INFOPANE_EDGE_BUFFER } from './markerConstants';

interface Clearance {
  above: number;
  below: number;
  left: number;
  right: number;
}

/**
 * Calculates available clearance in all four directions from marker position.
 *
 * @param markerX - Marker X coordinate in pixels
 * @param markerY - Marker Y coordinate in pixels
 * @param mapWidth - Map width in pixels
 * @param mapHeight - Map height in pixels
 * @param panelWidth - InfoPane width in pixels
 * @param panelHeight - InfoPane height in pixels
 * @param offset - Offset distance from marker
 * @returns Clearance object with space in each direction
 */
export function calculateClearanceInAllDirections(
  markerX: number,
  markerY: number,
  mapWidth: number,
  mapHeight: number,
  panelWidth: number,
  panelHeight: number,
  offset: number,
): Clearance {
  return {
    above: markerY - (panelHeight + offset),
    below: mapHeight - markerY - (panelHeight + offset),
    left: markerX - (panelWidth + offset),
    right: mapWidth - markerX - (panelWidth + offset),
  };
}

/**
 * Selects the best direction for InfoPane placement based on available clearance.
 * Uses a two-phase approach:
 * 1. If any direction has positive clearance, pick the one with most space
 * 2. If nothing fits, calculate fit ratios and pick the least bad option
 *
 * @param clearance - Available space in each direction
 * @param markerX - Marker X coordinate
 * @param markerY - Marker Y coordinate
 * @param mapWidth - Map width
 * @param mapHeight - Map height
 * @param panelWidth - InfoPane width
 * @param panelHeight - InfoPane height
 * @param offset - Offset distance
 * @returns The optimal direction for InfoPane placement
 */
export function selectBestDirection(
  clearance: Clearance,
  markerX: number,
  markerY: number,
  mapWidth: number,
  mapHeight: number,
  panelWidth: number,
  panelHeight: number,
  offset: number,
): InfoPanePosition {
  // Build array of viable options (those with positive clearance)
  const viableOptions = Object.entries(clearance)
    .filter(([_, clear]) => clear >= -INFOPANE_EDGE_BUFFER) // Allow slight overflow with buffer
    .map(([dir, clear]) => ({
      direction: dir as InfoPanePosition,
      clearance: clear,
    }))
    .sort((a, b) => b.clearance - a.clearance);

  if (viableOptions.length > 0) {
    // Pick the direction with the most clearance
    return viableOptions[0].direction;
  }

  // Nothing fits well, pick the least bad option by calculating fit ratios
  const fitRatios = {
    above: (markerY - INFOPANE_EDGE_BUFFER) / (panelHeight + offset),
    below: (mapHeight - markerY - INFOPANE_EDGE_BUFFER) / (panelHeight + offset),
    left: (markerX - INFOPANE_EDGE_BUFFER) / (panelWidth + offset),
    right: (mapWidth - markerX - INFOPANE_EDGE_BUFFER) / (panelWidth + offset),
  };

  return Object.entries(fitRatios).sort((a, b) => b[1] - a[1])[0][0] as InfoPanePosition;
}

export type InfoPanePositioning = {
  top?: string;
  right?: string;
  bottom?: string;
  left?: string;
  marginTop?: string;
  marginLeft?: string;
};

/**
 * Generates CSS positioning (top, right, bottom, left) for the given direction.
 *
 * @param direction - Direction to position the InfoPane
 * @param panelHeight - InfoPane height (needed for left/right centering)
 * @param panelWidth - InfoPane width (needed for horizontal centering)
 * @param offset - Offset distance from marker
 * @returns CSS positioning properties
 */
export function generatePositionForDirection(
  direction: InfoPanePosition,
  panelHeight: number,
  panelWidth: number,
  offset: number,
): InfoPanePositioning {
  switch (direction) {
    case 'below': {
      const horizontalOffset = -panelWidth / 2;
      return { left: `${horizontalOffset}px`, top: `${offset}px` };
    }

    case 'left': {
      const verticalOffset = -panelHeight / 2;
      return { right: `${offset}px`, top: `${verticalOffset}px` };
    }

    case 'right': {
      const verticalOffset = -panelHeight / 2;
      return { left: `${offset}px`, top: `${verticalOffset}px` };
    }

    default: {
      // 'above'
      const horizontalOffset = -panelWidth / 2;
      return { left: `${horizontalOffset}px`, bottom: `${offset}px` };
    }
  }
}
